import React, { useState, useRef, useEffect } from "react";
import { Check, Clock, StickyNote, Gift, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { EditableCell } from "@/components/EditableCell";
import { checkOmaggioConflict } from "@/utils/omaggiAlert";

/**
 * Single product row in the "Ordine Rapido" stop editor.
 * Handles selection toggle, qty/omaggio inputs, inline note editor, inline discount editor.
 */
export function GiroProductRow({
  product,
  entry,
  noteOpen,
  discountOpen,
  scontoCanale = 0,
  clientDiscount = null,
  showDate = true,
  contrattualizzato = false,
  recentOmaggio = null,
  onToggle,
  onNoteButtonClick,
  onDiscountButtonClick,
  onQtyChange,
  onOmChange,
  onNoteChange,
  onDiscountChange,
  justActivated = false,
  onActivationConsumed = () => {},
}) {
  const isSelected = entry !== undefined;
  const hasHistory = product.ultima_qta > 0;
  const hasReso = (product.ultimo_reso_qta || 0) > 0;
  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" });

  // For NEW articles (no past history) we require an explicit OK confirmation when adding.
  // Articles already ordered in the past (hasHistory) skip the confirmation — single tap adds.
  const needsConfirmOnAdd = !hasHistory && !hasReso;
  const [pendingAdd, setPendingAdd] = useState(false);
  const [pendingQty, setPendingQty] = useState(1);
  const pendingInputRef = useRef(null);

  // Auto-focus della cella qty quando l'articolo passa da non-selezionato a
  // selezionato. Su mobile apre anche la tastiera numerica per consentire
  // all'agente di digitare subito la quantità senza secondi tap.
  // Usiamo `justActivated` (controllato dal parent) perché il GiroProductRow
  // viene rimontato quando un articolo passa da "Altri articoli" a
  // "Selezionati" (cambia il `key`), quindi un ref locale `prevSelected` non
  // basta più. Il flag viene "consumato" subito dopo per evitare re-focus.
  const qtyCellRef = useRef(null);
  useEffect(() => {
    if (justActivated && isSelected) {
      const t = setTimeout(() => {
        qtyCellRef.current?.focus();
        onActivationConsumed();
      }, 80);
      return () => clearTimeout(t);
    }
  }, [justActivated, isSelected, onActivationConsumed]);
  useEffect(() => {
    if (pendingAdd) {
      setPendingQty(1);
      setTimeout(() => pendingInputRef.current?.focus(), 50);
    }
  }, [pendingAdd]);
  useEffect(() => {
    if (isSelected && pendingAdd) setPendingAdd(false);
  }, [isSelected, pendingAdd]);

  const confirmPendingAdd = () => {
    const q = Math.max(1, Number(pendingQty) || 1);
    onToggle(product.cod_articolo, q);
    setPendingAdd(false);
  };

  // Discount override flag: any non-zero sc* applied manually by agent
  const ovr = entry?.discount_override || null;
  const hasOverride = !!ovr && ((ovr.sc1 || 0) + (ovr.sc2 || 0) + (ovr.sc3 || 0) + (ovr.sc4 || 0)) > 0;
  // Effective discounts used for display/calculation: override > promo > rule
  const effective = hasOverride ? ovr : (clientDiscount && clientDiscount.source !== "manual" ? clientDiscount : null);
  const isManualClientPrice = !hasOverride && clientDiscount?.source === "manual" && (clientDiscount?.manual_price || 0) > 0;
  const isPromoNetPrice = !hasOverride && clientDiscount?.source === "promo" && (clientDiscount?.promo_net || 0) > 0;
  const isPromoActive = !hasOverride && clientDiscount?.source === "promo";

  const priceList = Number(product.price_list) || 0;
  const canale = Number(scontoCanale) || 0;
  const computeNetto = (disc) => {
    const s1 = Number(disc?.sc1 || 0);
    const s2 = Number(disc?.sc2 || 0);
    const s3 = Number(disc?.sc3 || 0);
    const s4 = Number(disc?.sc4 || 0);
    let n = priceList;
    for (const d of [s1, s2, s3, s4]) if (d > 0) n *= (1 - d / 100);
    if (canale > 0) n *= (1 - canale / 100);
    return n;
  };
  const currentNetto = isPromoNetPrice
    ? clientDiscount.promo_net  // promo netto fisso: NO sconto canale sopra
    : isManualClientPrice
    ? (clientDiscount.manual_price * (canale > 0 ? (1 - canale / 100) : 1))
    : computeNetto(effective);

  // ----------------------------------------------------------------------
  // "Prezzo da indicare" check (per fatturazione)
  // ----------------------------------------------------------------------
  // Per i prodotti NON Triolo, l'agente deve indicare un prezzo prima di
  // trasferire l'ordine, altrimenti il sistema fattura al listino senza
  // sconti — situazione che vogliamo evitare.
  // "Prezzo indicato" significa che il sistema ha un prezzo determinato:
  //   - prodotto Triolo (prezzi standard sempre validi)
  //   - prezzo fisso del cliente (client_prices)
  //   - promo attiva
  //   - override sconto inserito dall'agente (sc1-4 > 0)
  //   - regola sconto del cliente (sc1-4 > 0)
  const isTrioloProduct =
    String(product.macro_category || "").trim().toLowerCase() === "prodotti triolo";
  const hasAnyDiscount =
    !!effective &&
    (Number(effective.sc1 || 0) +
      Number(effective.sc2 || 0) +
      Number(effective.sc3 || 0) +
      Number(effective.sc4 || 0)) > 0;

  // Se l'ultimo prezzo pagato dal cliente è >= del netto base (listino × canale)
  // significa che storicamente compra al margine pieno: non serve mostrare il
  // warning, applicare uno sconto qui farebbe solo perdere marginalità.
  // Tolleranza ~0.5% (min 5 cent) per gestire arrotondamenti.
  const ultimoPrezzo = Number(product.ultimo_prezzo || 0);
  const nettoBase = priceList * (canale > 0 ? (1 - canale / 100) : 1);
  const tolerance = Math.max(0.05, nettoBase * 0.005);
  const lastPriceAtFullMargin = ultimoPrezzo > 0 && ultimoPrezzo >= (nettoBase - tolerance);

  const priceIsSpecified =
    isTrioloProduct ||
    isManualClientPrice ||
    isPromoNetPrice ||
    isPromoActive ||
    hasOverride ||
    hasAnyDiscount ||
    lastPriceAtFullMargin;
  const needsPriceWarning = isSelected && !priceIsSpecified;

  // Possibile "riproposta di reso": omaggio uguale/simile (±1) a uno recente (18 gg)
  const omaggioConflict = isSelected
    ? checkOmaggioConflict(product, entry?.qty_om, recentOmaggio)
    : null;

  return (
    <div data-testid={`giro-prod-${product.cod_articolo}`}>
      <div
        className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${
          needsPriceWarning
            ? "border-red-500/60 bg-red-500/5 ring-1 ring-red-500/30"
            : isSelected
            ? "border-emerald-500/30 bg-emerald-500/5"
            : pendingAdd
            ? "border-blue-400/50 bg-blue-500/10 ring-1 ring-blue-500/30"
            : product.non_disponibile
            ? "border-white/5 bg-[#18181b] opacity-50"
            : "border-white/5 bg-[#18181b] hover:border-white/10"
        } ${(noteOpen || discountOpen) ? "rounded-b-none" : ""}`}
        data-testid={needsPriceWarning ? `giro-prod-no-price-${product.cod_articolo}` : undefined}
      >
        <button
          onClick={() => {
            if (product.non_disponibile) return;
            if (isSelected) {
              // Deselect immediately (no confirmation for removal)
              onToggle(product.cod_articolo, 1);
              return;
            }
            // Articles with past history → add immediately. New ones → require confirmation.
            if (needsConfirmOnAdd) {
              setPendingAdd(true);
            } else {
              onToggle(product.cod_articolo, 1);
            }
          }}
          disabled={product.non_disponibile}
          className={`w-7 h-7 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
            isSelected
              ? "bg-emerald-500 border-emerald-500 text-white"
              : pendingAdd
              ? "border-blue-400 bg-blue-500/30 text-blue-100"
              : "bg-white border-white/30 hover:border-emerald-400"
          }`}
          data-testid={`toggle-${product.cod_articolo}`}
        >
          {isSelected && <Check className="w-4 h-4" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            {product._extra && (
              <Badge className="bg-blue-500/20 text-blue-400 text-[9px] px-1 py-0 h-4 shrink-0">+</Badge>
            )}
            <p className={`text-xs font-medium truncate ${product.non_disponibile ? "text-zinc-500 line-through" : "text-white"}`}>
              {product.name}
            </p>
          </div>
          <div className="flex items-center gap-x-2 gap-y-0.5 flex-wrap text-[10px] text-zinc-500">
            <span className="font-mono">{product.cod_articolo}</span>
            <span className="font-mono">{product.price_list?.toFixed(2)}</span>
            {isPromoActive && (
              <Badge className="bg-red-500/20 text-red-300 border border-red-500/30 text-[9px] px-1 py-0 h-4 shrink-0" data-testid={`promo-badge-${product.cod_articolo}`}>
                PROMO
              </Badge>
            )}
            {needsPriceWarning && (
              <Badge
                className="bg-red-500/25 text-red-200 border border-red-500/50 text-[9px] px-1 py-0 h-4 shrink-0 font-bold"
                title="Imposta sconti o prezzo manuale prima di trasferire l'ordine"
                data-testid={`no-price-badge-${product.cod_articolo}`}
              >
                ⚠️ PREZZO
              </Badge>
            )}
            {omaggioConflict && (
              <Badge
                className="bg-fuchsia-500/25 text-fuchsia-200 border border-fuchsia-500/50 text-[9px] px-1 py-0 h-4 shrink-0 font-bold"
                title={`Omaggio simile già fatto il ${new Date(omaggioConflict.data).toLocaleDateString("it-IT")} (${omaggioConflict.qta} pz) — possibile riproposta di reso da distruggere`}
                data-testid={`omaggio-alert-badge-${product.cod_articolo}`}
              >
                ⚠️ OMAGGIO {omaggioConflict.qta}× {new Date(omaggioConflict.data).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
              </Badge>
            )}
            {product.giacenza !== null && product.giacenza !== undefined && (
              <Badge
                data-testid={`giacenza-${product.cod_articolo}`}
                className={`text-[9px] px-1 py-0 h-4 shrink-0 border ${
                  product.giacenza > 0
                    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                    : "bg-red-500/15 text-red-300 border-red-500/30"
                }`}
                title="Giacenza disponibile"
              >
                Giac. {product.giacenza.toLocaleString("it-IT", { maximumFractionDigits: 0 })}
              </Badge>
            )}
            {hasHistory && (
              <span className="text-cyan-400 flex items-center gap-0.5 whitespace-nowrap" data-testid={`ultimo-ordine-${product.cod_articolo}`} title={`Ultimo acquisto: ${product.ultima_qta}${!contrattualizzato ? ` × € ${Number(product.ultimo_prezzo || 0).toFixed(3)}` : ""}${product.ultima_data ? ` il ${fmtDate(product.ultima_data)}` : ""}`}>
                <Clock className="w-2.5 h-2.5 shrink-0" />
                <span>{product.ultima_qta}</span>
                {!contrattualizzato && Number(product.ultimo_prezzo) > 0 && (
                  <span className="text-amber-300">·{Number(product.ultimo_prezzo).toFixed(2).replace(".", ",")}€</span>
                )}
                {showDate && product.ultima_data && (
                  <span className="text-zinc-600 ml-0.5 whitespace-nowrap">
                    {fmtDate(product.ultima_data)}
                  </span>
                )}
              </span>
            )}
            {hasReso && (
              <span className="text-fuchsia-400 flex items-center gap-0.5 whitespace-nowrap" data-testid={`ultimo-reso-${product.cod_articolo}`} title="Ultimo reso / omaggio">
                <Gift className="w-2.5 h-2.5 shrink-0" />
                {product.ultimo_reso_qta}x
                {showDate && product.ultimo_reso_data && (
                  <span className="text-zinc-600 ml-0.5 whitespace-nowrap">
                    {fmtDate(product.ultimo_reso_data)}
                  </span>
                )}
              </span>
            )}
            {isSelected && entry?.note?.trim() && !noteOpen && (
              <span className="text-amber-300/80 italic truncate max-w-[120px]">
                <StickyNote className="w-2.5 h-2.5 inline mr-0.5" />
                {entry.note}
              </span>
            )}
          </div>
        </div>

        {!isSelected && pendingAdd && (
          <div className="flex items-center gap-1.5 shrink-0">
            <Input
              ref={pendingInputRef}
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={pendingQty}
              onChange={(e) => setPendingQty(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") confirmPendingAdd();
                if (e.key === "Escape") setPendingAdd(false);
              }}
              className="h-8 w-14 bg-[#09090b] border-blue-400/50 text-white text-xs text-center font-mono"
              data-testid={`pending-qty-${product.cod_articolo}`}
            />
            <button
              type="button"
              onClick={confirmPendingAdd}
              data-testid={`pending-confirm-${product.cod_articolo}`}
              className="h-8 px-2 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1"
              title="Aggiungi (Invio)"
            >
              <Check className="w-3.5 h-3.5" /> OK
            </button>
            <button
              type="button"
              onClick={() => setPendingAdd(false)}
              data-testid={`pending-cancel-${product.cod_articolo}`}
              className="h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 text-zinc-300 flex items-center justify-center shrink-0"
              aria-label="Annulla"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {isSelected && (
          <>
            <button
              onClick={() => onNoteButtonClick(product.cod_articolo)}
              className={`p-1 rounded shrink-0 transition-colors ${
                entry?.note?.trim() ? "text-amber-400 hover:text-amber-300" : "text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid={`note-btn-${product.cod_articolo}`}
              title="Nota articolo"
            >
              <StickyNote className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDiscountButtonClick(product.cod_articolo)}
              disabled={contrattualizzato}
              className={`px-2 h-8 rounded-md shrink-0 transition-all border font-bold text-base flex items-center justify-center ${
                contrattualizzato
                  ? "text-zinc-700 border-zinc-800 bg-transparent cursor-not-allowed opacity-40"
                  : hasOverride
                    ? "text-emerald-300 border-emerald-400/60 bg-emerald-500/20 hover:bg-emerald-500/30 shadow-[0_0_8px_rgba(16,185,129,0.3)]"
                    : (effective || isManualClientPrice)
                      ? "text-cyan-300 border-cyan-400/60 bg-cyan-500/15 hover:bg-cyan-500/25"
                      : "text-amber-300 border-amber-400/60 bg-amber-500/15 hover:bg-amber-500/30 hover:text-amber-200"
              }`}
              data-testid={`discount-btn-${product.cod_articolo}`}
              title={contrattualizzato ? "Cliente contrattualizzato — prezzi non modificabili" : hasOverride ? "Sconti (override manuale)" : (effective || isManualClientPrice) ? "Sconti cliente memorizzati" : "Modifica sconto / prezzo netto"}
            >
              €
            </button>
            <div className="flex flex-col items-end gap-0.5 shrink-0">
              <div className="flex items-center gap-1">
                <EditableCell
                  ref={qtyCellRef}
                  value={entry.qty || 0}
                  decimals={0}
                  onChange={(v) => onQtyChange(product.cod_articolo, Math.max(0, v))}
                  className="w-12 h-7 text-center bg-[#09090b] border-emerald-500/30 text-emerald-400 text-xs"
                  data-testid={`qty-${product.cod_articolo}`}
                />
                <span className="text-zinc-600 text-[9px] w-6">vnd</span>
              </div>
              <div className="flex items-center gap-1">
                <EditableCell
                  value={entry.qty_om || 0}
                  decimals={0}
                  onChange={(v) => onOmChange(product.cod_articolo, v)}
                  className="w-12 h-7 text-center bg-[#09090b] border-fuchsia-500/30 text-fuchsia-400 text-xs"
                  data-testid={`qty-om-${product.cod_articolo}`}
                />
                <span className="text-zinc-600 text-[9px] w-6">om.</span>
              </div>
            </div>
          </>
        )}
      </div>

      {isSelected && noteOpen && (
        <div className="px-2 py-2 -mt-px rounded-b-lg border border-t-0 border-emerald-500/30 bg-emerald-500/5">
          <Input
            autoFocus
            value={entry.note || ""}
            onChange={(e) => onNoteChange(product.cod_articolo, e.target.value)}
            placeholder="Nota per questo articolo…"
            className="h-7 text-xs bg-[#09090b] border-amber-500/30 text-amber-200 placeholder:text-zinc-600"
            data-testid={`note-input-${product.cod_articolo}`}
          />
        </div>
      )}

      {isSelected && discountOpen && !contrattualizzato && (
        <div className="px-2 py-2 -mt-px rounded-b-lg border border-t-0 border-emerald-500/30 bg-emerald-500/5 space-y-2"
             data-testid={`discount-panel-${product.cod_articolo}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1 text-[10px] text-zinc-500">
              <span>Listino</span>
              <span className="font-mono text-zinc-300">€ {priceList.toFixed(2)}</span>
              {canale > 0 && (
                <>
                  <span className="text-zinc-600 mx-1">•</span>
                  <span className="text-cyan-400">Canale {canale}% (fisso)</span>
                </>
              )}
              {isManualClientPrice && (
                <>
                  <span className="text-zinc-600 mx-1">•</span>
                  <span className="text-cyan-400" title="Prezzo fisso memorizzato per questo cliente">
                    Prezzo fisso cliente € {clientDiscount.manual_price.toFixed(2)}
                  </span>
                </>
              )}
              {!hasOverride && effective && !isManualClientPrice && (
                <>
                  <span className="text-zinc-600 mx-1">•</span>
                  <span className="text-cyan-400" title="Sconti memorizzati per questo cliente (editabili)">Sconti cliente</span>
                </>
              )}
            </div>
            <div className="text-[10px] text-zinc-500">
              Netto <span className="font-mono text-emerald-400 text-xs" data-testid={`netto-${product.cod_articolo}`}>
                € {currentNetto.toFixed(3)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-emerald-500/70 text-[10px] mr-1">Sc%</span>
            {[1, 2, 3, 4].map((idx) => {
              const displayVal = hasOverride
                ? Number(ovr?.[`sc${idx}`] || 0)
                : Number(effective?.[`sc${idx}`] || 0);
              return (
                <EditableCell
                  key={idx}
                  value={displayVal}
                  decimals={2}
                  onChange={(v) => {
                    // Qualsiasi edit crea un override partendo dagli sconti correnti (effective)
                    const base = hasOverride
                      ? ovr
                      : (effective || { sc1: 0, sc2: 0, sc3: 0, sc4: 0 });
                    onDiscountChange(product.cod_articolo, {
                      sc1: Number(base.sc1 || 0),
                      sc2: Number(base.sc2 || 0),
                      sc3: Number(base.sc3 || 0),
                      sc4: Number(base.sc4 || 0),
                      [`sc${idx}`]: Math.max(0, Math.min(100, Number(v) || 0)),
                    });
                  }}
                  className="w-12 h-7 text-center bg-[#09090b] border-emerald-500/30 text-emerald-400 text-xs"
                  data-testid={`sc${idx}-${product.cod_articolo}`}
                />
              );
            })}
            {hasOverride && (
              <button
                onClick={() => onDiscountChange(product.cod_articolo, null)}
                className="ml-2 text-[10px] text-zinc-500 hover:text-red-400 underline decoration-dotted"
                data-testid={`reset-discount-${product.cod_articolo}`}
                title="Azzera override (torna agli sconti cliente automatici)"
              >
                Reset
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-zinc-500 shrink-0">oppure Netto €</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              max={priceList}
              defaultValue={hasOverride ? currentNetto.toFixed(3) : ""}
              placeholder={`max ${priceList.toFixed(2)}`}
              onBlur={(e) => {
                const n = parseFloat(e.target.value);
                if (isNaN(n) || n <= 0) return;
                if (priceList <= 0) return;
                // netto = listino * (1 - sc1/100) * (1 - canale/100)
                // => sc1 = (1 - netto / (listino * (1 - canale/100))) * 100
                const denom = priceList * (1 - canale / 100);
                if (denom <= 0) return;
                let sc1 = (1 - n / denom) * 100;
                sc1 = Math.max(0, Math.min(100, Math.round(sc1 * 100) / 100));
                onDiscountChange(product.cod_articolo, { sc1, sc2: 0, sc3: 0, sc4: 0 });
              }}
              className="h-7 text-xs bg-[#09090b] border-emerald-500/30 text-emerald-200 placeholder:text-zinc-600 font-mono max-w-[110px]"
              data-testid={`netto-input-${product.cod_articolo}`}
            />
            {/* Quick-fill da price_min / price_auth — solo per prodotti NON Triolo */}
            {product.macro_category !== "Prodotti Triolo" && (() => {
              const applyTargetNetto = (target) => {
                if (!target || priceList <= 0) return;
                const denom = priceList * (1 - canale / 100);
                if (denom <= 0) return;
                let sc1 = (1 - target / denom) * 100;
                sc1 = Math.max(0, Math.min(100, Math.round(sc1 * 100) / 100));
                onDiscountChange(product.cod_articolo, { sc1, sc2: 0, sc3: 0, sc4: 0 });
              };
              const pMin = Number(product.price_min) || 0;
              const pAuth = Number(product.price_auth) || 0;
              const pPromo = Number(product.price_promo) || 0;
              const pLast = Number(product.ultimo_prezzo) || 0;
              const lastDate = (product.ultima_data || "").slice(0, 10);
              return (
                <>
                  {pMin > 0 && (
                    <button
                      type="button"
                      onClick={() => applyTargetNetto(pMin)}
                      className="h-7 px-2 rounded border border-sky-500/40 bg-sky-500/10 text-sky-300 text-[10px] font-mono hover:bg-sky-500/20 transition-colors shrink-0"
                      data-testid={`quick-min-${product.cod_articolo}`}
                      title={`Applica prezzo minimo € ${pMin.toFixed(2)}`}
                    >
                      Min € {pMin.toFixed(2)}
                    </button>
                  )}
                  {pAuth > 0 && (
                    <button
                      type="button"
                      onClick={() => applyTargetNetto(pAuth)}
                      className="h-7 px-2 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10px] font-mono hover:bg-amber-500/20 transition-colors shrink-0"
                      data-testid={`quick-auth-${product.cod_articolo}`}
                      title={`Applica prezzo da autorizzare € ${pAuth.toFixed(2)}`}
                    >
                      Aut € {pAuth.toFixed(2)}
                    </button>
                  )}
                  {pPromo > 0 && (
                    <button
                      type="button"
                      onClick={() => applyTargetNetto(pPromo)}
                      className="h-7 px-2 rounded border border-red-500/40 bg-red-500/10 text-red-300 text-[10px] font-mono hover:bg-red-500/20 transition-colors shrink-0"
                      data-testid={`quick-promo-${product.cod_articolo}`}
                      title={`Applica prezzo promo € ${pPromo.toFixed(2)}`}
                    >
                      Promo € {pPromo.toFixed(2)}
                    </button>
                  )}
                  {pLast > 0 && (
                    <button
                      type="button"
                      onClick={() => applyTargetNetto(pLast)}
                      className="h-7 px-2 rounded border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300 text-[10px] font-mono hover:bg-fuchsia-500/20 transition-colors shrink-0"
                      data-testid={`quick-last-${product.cod_articolo}`}
                      title={`Ultimo prezzo praticato € ${pLast.toFixed(3)}${lastDate ? ` (${lastDate})` : ""}`}
                    >
                      Ult. € {pLast.toFixed(3)}
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Read-only single product row for locked orders (other agent / already transferred).
 */
export function GiroProductRowReadOnly({ product, entry }) {
  return (
    <div
      data-testid={`giro-prod-${product.cod_articolo}`}
      className="flex items-center gap-2 p-2 rounded-lg border border-white/5 bg-[#18181b] opacity-90"
    >
      <div className="w-7 h-7 rounded-md border border-white/10 bg-white/5 flex items-center justify-center shrink-0">
        <Check className="w-4 h-4 text-zinc-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate text-zinc-300">{product.name}</p>
        <div className="flex items-center gap-2 text-[10px] text-zinc-500">
          <span className="font-mono">{product.cod_articolo}</span>
          <span className="font-mono">{product.price_list?.toFixed(2)}</span>
        </div>
        {entry.note && (
          <p className="text-[10px] text-amber-300/70 italic mt-0.5 truncate">
            <StickyNote className="w-2.5 h-2.5 inline mr-0.5" />
            {entry.note}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-0 shrink-0 text-[11px] font-mono">
        <span className="text-emerald-400">{entry.qty || 0} vnd</span>
        {entry.qty_om > 0 && <span className="text-fuchsia-400">{entry.qty_om} om.</span>}
      </div>
    </div>
  );
}
